import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { TextModal as TheOneModal } from './one-modal-to-rule-them-all';
import MaterialIcon from './MaterialIcon';
import LoadingStatusPanel from './LoadingStatusPanel';
import EasyButton from './EasyButton';
import ClothedEyeLoader from './ClothedEyeLoader';
import databaseService from '../services/databaseService';
import etsyDataService from '../services/etsyDataService';
import dimensionExtractor from '../services/dimensionExtractor.js';
import loadingService from '../services/loadingService';
import heroSceneSettings from '../services/heroSceneSettings';
import logger from '../utils/logger.js';
import './AdminPanel.css';

function AdminPanel() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState({
    reactApp: 'pending',
    database: 'pending'
  });
  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    content: ''
  });
  const [debugAccordionOpen, setDebugAccordionOpen] = useState(false);
  const [singleListingId, setSingleListingId] = useState('');
  
  // Hero scene settings state
  const [heroLeftId, setHeroLeftId] = useState('');
  const [heroRightId, setHeroRightId] = useState('');
  
  // Load hero scene settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      const settings = await heroSceneSettings.getListingIds();
      setHeroLeftId(settings.leftListingId || '');
      setHeroRightId(settings.rightListingId || '');
    };
    loadSettings();
  }, []);

  // Loading panel state
  const [loadingPanelState, setLoadingPanelState] = useState({
    isVisible: false,
    progress: null,
    messages: []
  });

  useEffect(() => {
    // Test all components
    testComponents();
  }, []);

  // Listen to loading service updates
  useEffect(() => {
    const unsubscribe = loadingService.addListener((state) => {
      setLoadingPanelState(state);
    });
    
    return unsubscribe;
  }, []);

  const testComponents = async () => {
    try {
      // Test React App
      setStatus(prev => ({ ...prev, reactApp: 'success' }));

      // Test Database
      try {
        const result = await databaseService.getUnifiedStickers({ limit: 1000 });
        const stickers = result.data || [];
        setStatus(prev => ({ ...prev, database: 'success' }));
      } catch (dbError) {
        console.error('Database test failed:', dbError);
        setStatus(prev => ({ ...prev, database: 'error' }));
      }

    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };


  if (isLoading) {
    return (
      <div className="admin-panel">
        <div className="loading-container">
          <ClothedEyeLoader 
            size="large" 
            showTagline={true}
            message="Loading Admin Panel..."
          />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-panel">
        <div className="error-container">
          <h2>Error</h2>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'pending':
        return '⏳';
      default:
        return '❓';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'success':
        return 'Running';
      case 'error':
        return 'Failed';
      case 'pending':
        return 'Setting up...';
      default:
        return 'Unknown';
    }
  };

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <h1>ClothedEye Admin Panel</h1>
        <p>System Management & Testing</p>
      </header>
      
      <main className="admin-main">
        <div className="container">
          <div className="app-status">
            <h2>System Status</h2>
            <div className="status-grid">
              <div className="status-item">
                <span className="status-label">React App:</span>
                <span className={`status-value ${status.reactApp}`}>
                  {getStatusIcon(status.reactApp)} {getStatusText(status.reactApp)}
                </span>
              </div>
              <div className="status-item">
                <span className="status-label">Database:</span>
                <span className={`status-value ${status.database}`}>
                  {getStatusIcon(status.database)} {getStatusText(status.database)}
                </span>
              </div>

            </div>
          </div>

          <div className="etsy-connection">
            <h2>Etsy Connection</h2>
            <p>Connect to your Etsy account to access shop data:</p>
            <a 
              href="/oauth-simple.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="connect-btn"
            >
              <MaterialIcon icon="link" size={16} />
              Connect to Etsy
            </a>
            <p className="help-text">Opens in a new window. After connecting, return here to continue.</p>
          </div>

          <div className="token-status">
            <h2>Token Status</h2>
            <div className="token-info">
              {(() => {
                const token = localStorage.getItem('etsy_access_token');
                if (token) {
                  return (
                    <>
                      <span className="status-value success">✅</span>
                      <span className="token-text">Token Found: {token.substring(0, 20)}...</span>
                      <button 
                        className="clear-token-btn"
                        onClick={() => {
                          localStorage.removeItem('etsy_access_token');
                          window.location.reload();
                        }}
                      >
                        Clear Token
                      </button>
                    </>
                  );
                } else {
                  return (
                    <>
                      <span className="status-value error">❌</span>
                      <span className="token-text">No token found</span>
                    </>
                  );
                }
              })()}
            </div>
          </div>

          {/* Hero Scene Configuration */}
          <div className="hero-scene-settings">
            <h2>
              <MaterialIcon icon="settings" size={20} />
              Hero Scene Configuration
            </h2>
            <p className="help-text">
              Specify which 2 listings to display on the hero scene machine (left and right ports). 
              Must be micro-size stickers with SVG paths. This makes loading instant (~100ms).
            </p>
            <div className="hero-settings-form">
              <div className="hero-input-group">
                <label htmlFor="hero-left">Left Port Listing ID:</label>
                <input
                  id="hero-left"
                  type="text"
                  placeholder="e.g., 846621034"
                  value={heroLeftId}
                  onChange={(e) => setHeroLeftId(e.target.value)}
                  className="hero-input"
                />
              </div>
              <div className="hero-input-group">
                <label htmlFor="hero-right">Right Port Listing ID:</label>
                <input
                  id="hero-right"
                  type="text"
                  placeholder="e.g., 991051922"
                  value={heroRightId}
                  onChange={(e) => setHeroRightId(e.target.value)}
                  className="hero-input"
                />
              </div>
              <div className="hero-buttons">
                <button
                  className="btn btn-primary"
                  onClick={async () => {
                    if (!heroLeftId || !heroRightId) {
                      setModalState({
                        isOpen: true,
                        title: '⚠️ Missing IDs',
                        content: 'Please enter both listing IDs'
                      });
                      return;
                    }
                    
                    console.log('💾 Attempting to save:', { left: heroLeftId, right: heroRightId });
                    const success = await heroSceneSettings.save(heroLeftId, heroRightId);
                    
                    // Verify it was saved
                    const reloaded = await heroSceneSettings.getListingIds();
                    console.log('💾 Verification - Service says:', reloaded);
                    
                    if (success && reloaded.leftListingId && reloaded.rightListingId) {
                      setModalState({
                        isOpen: true,
                        title: '✅ Hero Scene Updated',
                        content: `Hero scene will now use:\nLeft: ${heroLeftId}\nRight: ${heroRightId}\n\n✅ Saved to database\n\nRefresh the homepage to see changes.`
                      });
                      
                      // Clear hero cache so new settings take effect
                      delete window.__heroStickerCache;
                    } else {
                      setModalState({
                        isOpen: true,
                        title: '❌ Save Failed',
                        content: `Failed to save hero scene settings.\n\nSuccess: ${success}\nReloaded: ${JSON.stringify(reloaded)}\n\nCheck console for details.`
                      });
                    }
                  }}
                >
                  <MaterialIcon icon="save" size={16} />
                  Save Settings
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={async () => {
                    await heroSceneSettings.clear();
                    setHeroLeftId('');
                    setHeroRightId('');
                    delete window.__heroStickerCache;
                    setModalState({
                      isOpen: true,
                      title: '✅ Settings Cleared',
                      content: 'Hero scene will now use random stickers. Refresh to see changes.'
                    });
                  }}
                >
                  <MaterialIcon icon="delete" size={16} />
                  Clear & Use Random
                </button>
              </div>
              {heroSceneSettings.isConfigured() && (
                <div className="hero-status">
                  <MaterialIcon icon="check_circle" size={16} />
                  <span>Currently configured: Left={heroLeftId}, Right={heroRightId}</span>
                </div>
              )}
            </div>
          </div>

          {/* Main Etsy Data Operations */}
          <div className="main-etsy-operations">
            <h2>Etsy Data Operations</h2>
            <div className="main-operations-grid">
              <EasyButton
                size="large"
                icon="cloud_download"
                iconSize={24}
                onClick={async () => {
                  try {
                    setModalState({
                      isOpen: true,
                      title: '🔄 Fetching All Etsy Data...',
                      content: '🔄 Starting to fetch all listings and sales data from your Etsy shop. This may take several minutes depending on the number of listings...'
                    });
                    
                    // Step 1: Fetch all listings
                    const result = await etsyDataService.fetchAndStoreAllListings();
                    
                    // Step 2: Fetch sales/transaction data
                    setModalState({
                      isOpen: true,
                      title: '🔄 Fetching Sales Data...',
                      content: '🔄 Listings complete! Now fetching sales transaction data from Etsy...'
                    });
                    
                    let salesResult = { success: false, transactionCount: 0, error: null };
                    try {
                      // Get stored access token from localStorage
                      const token = localStorage.getItem('etsy_access_token');
                      if (!token) {
                        throw new Error('No OAuth token found - please connect to Etsy first');
                      }
                      
                      // Call the Etsy transactions API with authorization
                      const salesResponse = await fetch('/.netlify/functions/etsy-shop-transactions', {
                        headers: {
                          'Authorization': `Bearer ${token}`
                        }
                      });
                      
                      if (salesResponse.ok) {
                        const salesData = await salesResponse.json();
                        
                        // Store the transaction data
                        if (salesData.transactions && salesData.transactions.length > 0) {
                          const storeResponse = await fetch('/.netlify/functions/local-db/shop-transactions', {
                            method: 'POST',
                            headers: { 'Content-Type': 'application/json' },
                            body: JSON.stringify({ transactions: salesData.transactions })
                          });
                          
                          if (storeResponse.ok) {
                            const storeResult = await storeResponse.json();
                            salesResult = { 
                              success: true, 
                              transactionCount: salesData.transactions.length,
                              salesSummary: salesData.salesSummary || {}
                            };
                          } else {
                            throw new Error('Failed to store transaction data');
                          }
                        } else {
                          salesResult = { success: true, transactionCount: 0, message: 'No transactions found' };
                        }
                      } else {
                        // Try to parse error as JSON, but handle plain text errors too
                        let errorMessage = 'Failed to fetch sales data';
                        try {
                          const errorData = await salesResponse.json();
                          errorMessage = errorData.error || errorMessage;
                        } catch (parseError) {
                          // If JSON parse fails, try to get text
                          try {
                            errorMessage = await salesResponse.text();
                          } catch (textError) {
                            errorMessage = `HTTP ${salesResponse.status}`;
                          }
                        }
                        throw new Error(errorMessage);
                      }
                    } catch (salesError) {
                      console.error('Sales data fetch failed:', salesError);
                      salesResult = { success: false, error: salesError.message };
                    }
                    
                    // Build comprehensive result message
                    let message = '✅ Etsy Data Fetch Complete!\n\n';
                    
                    // Listings summary
                    message += `📋 LISTINGS DATA:\n`;
                    message += `   Total Listings Found: ${result.totalListings}\n`;
                    message += `   Successfully Processed: ${result.successful}\n`;
                    message += `   Failed to Process: ${result.failed}\n\n`;
                    
                    // Sales data summary
                    message += `💰 SALES DATA:\n`;
                    if (salesResult.success) {
                      message += `   Transactions Fetched: ${salesResult.transactionCount}\n`;
                      if (salesResult.salesSummary) {
                        message += `   Unique Listings with Sales: ${Object.keys(salesResult.salesSummary).length}\n`;
                        message += `   Total Items Sold: ${Object.values(salesResult.salesSummary).reduce((sum, data) => sum + data.totalQuantity, 0)}\n`;
                      }
                      if (salesResult.message) {
                        message += `   Status: ${salesResult.message}\n`;
                      }
                    } else {
                      message += `   ❌ Failed: ${salesResult.error}\n`;
                      message += `   Note: Sales data requires valid OAuth token\n`;
                    }
                    message += '\n';
                    
                    if (result.successful > 0) {
                      message += `✅ SUCCESSFUL LISTINGS:\n`;
                      result.results.forEach((listing, index) => {
                        message += `   ${index + 1}. ${listing.listingId} - ${listing.data.listing?.title || 'Unknown'}\n`;
                      });
                      message += '\n';
                    }
                    
                    if (result.failed > 0) {
                      message += `❌ FAILED LISTINGS:\n`;
                      result.errors.forEach((error, index) => {
                        message += `   ${index + 1}. ${error.listingId} - ${error.error}\n`;
                      });
                    }
                    
                    setModalState({
                      isOpen: true,
                      title: '✅ All Etsy Data Fetch Complete',
                      content: message
                    });
                  } catch (error) {
                    console.error('❌ Fetch all Etsy data failed:', error);
                    setModalState({
                      isOpen: true,
                      title: '❌ Fetch All Etsy Data Failed',
                      content: '❌ Failed to fetch all Etsy data: ' + error.message
                    });
                  }
                }}
              >
                Fetch All Etsy Data (WORKING SYSTEM)
              </EasyButton>

              {/* Single Listing Fetch */}
              <div style={{ 
                display: 'flex', 
                gap: '10px', 
                alignItems: 'center',
                marginTop: '10px',
                padding: '15px',
                background: 'rgba(52, 152, 219, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(52, 152, 219, 0.3)'
              }}>
                <input
                  type="text"
                  value={singleListingId}
                  onChange={(e) => setSingleListingId(e.target.value)}
                  placeholder="Enter Listing ID (e.g., 853757823)"
                  style={{
                    flex: 1,
                    padding: '10px 15px',
                    fontSize: '14px',
                    border: '2px solid rgba(52, 152, 219, 0.5)',
                    borderRadius: '6px',
                    outline: 'none',
                    transition: 'border-color 0.2s',
                    backgroundColor: 'rgba(255, 255, 255, 0.9)'
                  }}
                  onFocus={(e) => e.target.style.borderColor = '#3498db'}
                  onBlur={(e) => e.target.style.borderColor = 'rgba(52, 152, 219, 0.5)'}
                />
                <EasyButton
                  icon="download"
                  variant="primary"
                  disabled={!singleListingId.trim()}
                  onClick={async () => {
                    const listingId = singleListingId.trim();
                    if (!listingId) return;

                    try {
                      setModalState({
                        isOpen: true,
                        title: '🔄 Fetching Listing...',
                        content: `🔄 Fetching data for listing ${listingId} from Etsy...`
                      });

                      // Check if we have a valid token
                      if (!etsyDataService.hasValidToken()) {
                        setModalState({
                          isOpen: true,
                          title: '❌ No Valid Token',
                          content: '❌ No valid Etsy token found. Please ensure you have a fresh token set in localStorage:\n\nlocalStorage.setItem("etsy_access_token", "YOUR_TOKEN")'
                        });
                        return;
                      }

                      // Fetch and store the listing
                      const result = await etsyDataService.fetchAndStoreListing(listingId);

                      if (!result.success) {
                        throw new Error(result.error || 'Failed to fetch listing');
                      }

                      // Build success message
                      let message = '✅ Successfully fetched and stored listing data!\n\n';
                      message += `📋 Listing ID: ${result.listingId}\n`;
                      message += `📝 Title: ${result.data?.listing?.title || 'N/A'}\n`;
                      message += `🏷️  State: ${result.data?.listing?.state || 'N/A'}\n`;
                      message += `🖼️  Images: ${result.data?.images?.results?.length || 0}\n`;
                      message += `📦 Products: ${result.data?.inventory?.products?.length || 0}\n`;
                      message += `🎨 Variation Images: ${result.data?.variationImages?.count || 0}\n\n`;

                      // Add variation details
                      if (result.data?.extractedVariations) {
                        const variations = result.data.extractedVariations;
                        message += `🎨 Extracted Variations:\n`;
                        message += `   Sizes: ${variations.sizes?.length || 0} (${variations.sizes?.join(', ') || 'none'})\n`;
                        message += `   Colors: ${variations.colors?.length || 0} (${variations.colors?.join(', ') || 'none'})\n`;
                        
                        if (variations.completeness) {
                          message += `   Completeness: ${variations.completeness.captured}/${variations.completeness.total} products\n`;
                          
                          if (!variations.completeness.is_complete) {
                            message += `\n⚠️ Variation extraction issues: ${variations.completeness.issues?.join(', ') || 'unknown'}\n`;
                          }
                        }
                      }

                      // Add dimensions if extracted
                      if (result.data?.dimensions && Object.keys(result.data.dimensions).length > 0) {
                        message += `\n📏 Extracted Dimensions:\n`;
                        Object.keys(result.data.dimensions).forEach(size => {
                          const dim = result.data.dimensions[size];
                          if (dim && dim.width && dim.height) {
                            message += `   ${size}: ${dim.width}w x ${dim.height}h ${dim.unit || 'inches'}\n`;
                          }
                        });
                      }

                      message += '\n✅ Data has been saved to local database\n\n';
                      message += '💡 To add variation IDs for better deep-links:\n';
                      message += '   Run EtsyCrawler project to scrape variation IDs\n';
                      message += '   See: docs/ETSY_VARIATION_IDS.md';

                      setModalState({
                        isOpen: true,
                        title: '✅ Listing Fetched Successfully',
                        content: message
                      });

                      // Clear the input
                      setSingleListingId('');

                    } catch (error) {
                      console.error('❌ Failed to fetch listing:', error);
                      
                      let errorMessage = `❌ Failed to fetch listing ${listingId}\n\n`;
                      errorMessage += `Error: ${error.message}\n\n`;

                      // Check for specific error types
                      if (error.message.includes('401') || error.message.includes('authorization')) {
                        errorMessage += '💡 Token may be invalid or expired. Try getting a fresh token.';
                      } else if (error.message.includes('404')) {
                        errorMessage += '💡 Listing may not exist or may not be accessible.';
                      } else if (error.message.includes('429')) {
                        errorMessage += '💡 Rate limit exceeded. Wait a moment and try again.';
                      } else {
                        errorMessage += '💡 Check network connection and API status.';
                      }

                      setModalState({
                        isOpen: true,
                        title: '❌ Fetch Failed',
                        content: errorMessage
                      });
                    }
                  }}
                >
                  Fetch Single Listing
                </EasyButton>
              </div>

              {/* SAFE UPDATE - Recommended */}
              <EasyButton
                size="large"
                icon="shield"
                iconSize={24}
                onClick={async () => {
                  try {
                    setModalState({
                      isOpen: true,
                      title: '🛡️ Safe Update from Etsy...',
                      content: '🛡️ Starting safe update process. This will:\n\n✅ Update titles from Etsy\n✅ Update descriptions, prices, and inventory\n✅ PRESERVE all scraped variation data\n✅ PRESERVE all manually entered dimensions\n✅ PRESERVE all local settings (visibility, display order, etc.)\n\n📊 This may take several minutes...'
                    });

                    // Use the safe update function
                    const result = await etsyDataService.safeUpdateAllListingsFromEtsy();
                    
                    let message = '✅ Safe Update Complete!\n\n';
                    message += `📋 Total Listings: ${result.total}\n`;
                    message += `✅ Successfully Updated: ${result.successful}\n`;
                    message += `❌ Failed to Update: ${result.failed}\n\n`;
                    
                    if (result.successful > 0) {
                      message += `✅ SUCCESSFULLY UPDATED:\n`;
                      result.updates.slice(0, 10).forEach((update, index) => {
                        message += `   ${index + 1}. ${update.listingId} - ${update.title}\n`;
                      });
                      if (result.updates.length > 10) {
                        message += `   ... and ${result.updates.length - 10} more\n`;
                      }
                      message += '\n';
                    }
                    
                    if (result.failed > 0) {
                      message += `❌ FAILED UPDATES:\n`;
                      result.errors.forEach((error, index) => {
                        message += `   ${index + 1}. ${error.listingId} - ${error.title}\n`;
                        message += `      Error: ${error.error}\n`;
                      });
                    }
                    
                    message += '\n🔒 All local data (variations, dimensions, settings) has been preserved!';
                    
                    setModalState({
                      isOpen: true,
                      title: '✅ Safe Update Complete',
                      content: message
                    });
                  } catch (error) {
                    console.error('❌ Safe update failed:', error);
                    setModalState({
                      isOpen: true,
                      title: '❌ Safe Update Failed',
                      content: '❌ Failed to safely update from Etsy: ' + error.message
                    });
                  }
                }}
                style={{ background: 'linear-gradient(135deg, #27ae60 0%, #229954 100%)' }}
              >
                Safe Update from Etsy (RECOMMENDED)
              </EasyButton>
              
              {/* DANGEROUS - Process Etsy Data */}
              <button 
                className="btn"
                style={{ 
                  background: 'linear-gradient(135deg, #e74c3c 0%, #c0392b 100%)',
                  border: '2px solid #c0392b'
                }}
                onClick={async () => {
                  // Confirm before running dangerous operation
                  if (!window.confirm('⚠️ WARNING: This uses the deprecated update method and may corrupt local data!\n\nIt is recommended to use "Safe Update from Etsy" instead.\n\nAre you sure you want to continue?')) {
                    return;
                  }

                  try {
                    setModalState({
                      isOpen: true,
                      title: '🔄 Processing Etsy Data...',
                      content: '🔄 Processing and transforming Etsy data into usable format for the app...'
                    });

                    // Get all stored listing data (UNIFIED)
                    const response = await fetch('/.netlify/functions/local-db/unified-stickers');

                    if (!response.ok) {
                      throw new Error(`Failed to get stored data: ${response.statusText}`);
                    }

                    const data = await response.json();
                    const listings = data.data || [];

                    let processedCount = 0;
                    let errorCount = 0;
                    const errors = [];

                    for (const listing of listings) {
                      try {
                        // Process each listing - extract dimensions, update stickers table, etc.
                        const listingData = JSON.parse(listing.listing_data);
                        const inventoryData = JSON.parse(listing.inventory_data);
                        const extractedVariations = JSON.parse(listing.extracted_variations);
                        
                        // Extract dimensions from description
                        const dimensions = dimensionExtractor.extractAllDimensions({
                          listing: listingData,
                          inventory: inventoryData,
                          extractedVariations: extractedVariations
                        });
                        
                        // Update sticker with processed data
                        await etsyDataService.updateStickerWithEtsyData(listing.etsyListingId, {
                          listing: listingData,
                          inventory: inventoryData,
                          extractedVariations: extractedVariations,
                          dimensions: dimensions
                        });

                        processedCount++;
                      } catch (error) {
                        errorCount++;
                        errors.push({
                          listingId: listing.etsyListingId,
                          error: error.message
                        });
                      }
                    }
                    
                    let message = '✅ Successfully processed Etsy data!\n\n';
                    message += `📋 Total Listings Processed: ${listings.length}\n`;
                    message += `✅ Successfully Processed: ${processedCount}\n`;
                    message += `❌ Failed to Process: ${errorCount}\n\n`;
                    
                    if (errorCount > 0) {
                      message += `❌ PROCESSING ERRORS:\n`;
                      errors.forEach((error, index) => {
                        message += `   ${index + 1}. ${error.listingId} - ${error.error}\n`;
                      });
                    }
                    
                    setModalState({
                      isOpen: true,
                      title: '✅ Process Etsy Data Complete',
                      content: message
                    });
                  } catch (error) {
                    console.error('❌ Process Etsy data failed:', error);
                    setModalState({
                      isOpen: true,
                      title: '❌ Process Etsy Data Failed',
                      content: '❌ Failed to process Etsy data: ' + error.message
                    });
                  }
                }}
              >
                <MaterialIcon icon="warning" size={20} />
                Process Etsy Data (DEPRECATED - MAY CORRUPT DATA)
              </button>
            </div>
          </div>

          <div className="debug-section">
            <h2>Debug Information</h2>
            
            <div className="debug-grid">
              <div className="debug-item">
                <h3>Database Test</h3>
                <button 
                  className="btn"
                  onClick={async () => {
                    try {
                      const result = await databaseService.getUnifiedStickers({ limit: 1000 });
                      const stickers = result.data || [];
                      logger.log('All stickers:', stickers);
                      setModalState({
                        isOpen: true,
                        title: '✅ Database Test Success',
                        content: `Found ${stickers.length} stickers in database`
                      });
                    } catch (error) {
                      console.error('Database test failed:', error);
                      setModalState({
                        isOpen: true,
                        title: '❌ Database Test Failed',
                        content: 'Database test failed: ' + error.message
                      });
                    }
                  }}
                >
                  Test Database Connection
                </button>
              </div>

              <div className="debug-item">
                <h3>Etsy Data Test</h3>
                <button 
                  className="btn"
                  onClick={async () => {
                    try {
                      const etsyData = await databaseService.getEtsyListingData('846621034');
                      logger.log('Etsy data:', etsyData);
                      setModalState({
                        isOpen: true,
                        title: '✅ Etsy Data Test',
                        content: etsyData ? 'Etsy data found!' : 'No Etsy data found'
                      });
                    } catch (error) {
                      console.error('Etsy data test failed:', error);
                      setModalState({
                        isOpen: true,
                        title: '❌ Etsy Data Test Failed',
                        content: 'Etsy data test failed: ' + error.message
                      });
                    }
                  }}
                >
                  Test Etsy Data
                </button>
              </div>
            </div>
          </div>

          {/* Debug Functions Accordion */}
          <div className="debug-accordion">
            <button 
              className="accordion-header"
              onClick={() => setDebugAccordionOpen(!debugAccordionOpen)}
            >
              <h2>Debug Functions</h2>
              <MaterialIcon 
                icon={debugAccordionOpen ? "expand_less" : "expand_more"} 
                size={24} 
                color="#bdc3c7"
              />
            </button>
            
            {debugAccordionOpen && (
              <div className="accordion-content">
                <div className="test-section">
                  <h3>New Etsy API Functions Test</h3>
                  <div className="test-grid">
                    <button 
                      className="btn"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('etsy_access_token');
                          if (!token) {
                            setModalState({
                              isOpen: true,
                              title: '❌ No Token Found',
                              content: 'Please connect to Etsy first.'
                            });
                            return;
                          }

                          logger.log('🔍 Testing complete listing data...');
                          const response = await fetch('/.netlify/functions/etsy-complete-listing/846621034', {
                            headers: {
                              'Authorization': `Bearer ${token}`
                            }
                          });

                          if (response.ok) {
                            const data = await response.json();
                            logger.log('✅ Complete listing data:', data);
                            
                            let message = '✅ COMPLETE LISTING DATA TEST\n';
                            message += '=====================================\n\n';
                            message += `📋 Listing: ${data.listing?.title || 'Unknown'}\n`;
                            message += `🖼️ Images: ${data.images?.results?.length || 0} images\n`;
                            message += `📦 Inventory: ${data.inventory?.products?.length || 0} products\n`;
                            
                            if (data.extractedVariations) {
                              message += `🎨 Unique Sizes: ${data.extractedVariations.sizes.length}\n`;
                              message += `   ${data.extractedVariations.sizes.join(', ')}\n\n`;
                              message += `🎨 Unique Colors: ${data.extractedVariations.colors.length}\n`;
                              message += `   ${data.extractedVariations.colors.join(', ')}\n\n`;
                            }
                            
                            if (data.variationImages) {
                              message += `🎯 Variation Images: ${data.variationImages.count || 0} mappings\n`;
                              if (data.variationImages.results && data.variationImages.results.length > 0) {
                                message += '🔗 OFFICIAL ETsy MAPPINGS:\n';
                                data.variationImages.results.forEach((variation, index) => {
                                  message += `   ${variation.value} → Image ID: ${variation.image_id}\n`;
                                });
                              }
                            } else {
                              message += `🎯 Variation Images: No official mapping found\n`;
                            }
                            
                            setModalState({
                              isOpen: true,
                              title: '✅ Complete Listing Data',
                              content: message
                            });
                          } else {
                            const errorData = await response.json();
                            console.error('❌ API call failed:', errorData);
                            setModalState({
                              isOpen: true,
                              title: '❌ API Call Failed',
                              content: `❌ API call failed: ${errorData.error || response.statusText}`
                            });
                          }
                        } catch (error) {
                          console.error('❌ Etsy API test failed:', error);
                          setModalState({
                            isOpen: true,
                            title: '❌ Etsy API Test Failed',
                            content: '❌ Etsy API test failed: ' + error.message
                          });
                        }
                      }}
                    >
                      Test New Etsy API Functions
                    </button>
                    
                    <button 
                      className="btn"
                      onClick={async () => {
                        try {
                          // Test individual functions
                          const functions = [
                            { name: 'Shop Listings', url: '/.netlify/functions/etsy-shop-listings' },
                            { name: 'Listing Details', url: '/.netlify/functions/etsy-listing/846621034' },
                            { name: 'Listing Images', url: '/.netlify/functions/etsy-listing-images/846621034' },
                            { name: 'Listing Inventory', url: '/.netlify/functions/etsy-listing-inventory/846621034' }
                          ];
                          
                          let results = [];
                          
                          for (const func of functions) {
                            try {
                              const response = await fetch(func.url, {
                                headers: {
                                  'Authorization': 'Bearer ' + localStorage.getItem('etsy_access_token')
                                }
                              });
                              
                              if (response.ok) {
                                const data = await response.json();
                                results.push(`✅ ${func.name}: Success`);
                                logger.log(`${func.name} data:`, data);
                              } else {
                                results.push(`❌ ${func.name}: ${response.status}`);
                              }
                            } catch (error) {
                              results.push(`❌ ${func.name}: ${error.message}`);
                            }
                          }
                          
                          setModalState({
                            isOpen: true,
                            title: '🧪 Individual Function Tests',
                            content: 'Individual Function Tests:\n\n' + results.join('\n')
                          });
                        } catch (error) {
                          console.error('❌ Individual functions test failed:', error);
                          setModalState({
                            isOpen: true,
                            title: '❌ Individual Functions Test Failed',
                            content: '❌ Individual functions test failed: ' + error.message
                          });
                        }
                      }}
                    >
                      Test Individual Functions
                    </button>
                    
                    <button 
                      className="btn"
                      onClick={async () => {
                        try {
                          // Test the working etsy-users-me function first
                          const token = localStorage.getItem('etsy_access_token');
                          logger.log('🔑 Token from localStorage:', token ? token.substring(0, 20) + '...' : 'No token found');
                          
                          if (!token) {
                            setModalState({
                              isOpen: true,
                              title: '❌ No Token Found',
                              content: '❌ No token found in localStorage. Please connect to Etsy first using the oauth-simple.html page.'
                            });
                            return;
                          }
                          
                          const response = await fetch('/.netlify/functions/etsy-users-me', {
                            headers: {
                              'Authorization': `Bearer ${token}`
                            }
                          });
                          
                          if (response.ok) {
                            const data = await response.json();
                            logger.log('✅ etsy-users-me response:', data);
                            setModalState({
                              isOpen: true,
                              title: '✅ Token Test Success',
                              content: `✅ Token works! User: ${data.results?.[0]?.login_name || 'Unknown'}`
                            });
                          } else {
                            const errorData = await response.json();
                            console.error('❌ etsy-users-me failed:', errorData);
                            setModalState({
                              isOpen: true,
                              title: '❌ Token Test Failed',
                              content: `❌ Token test failed: ${errorData.error || response.statusText}`
                            });
                          }
                        } catch (error) {
                          console.error('❌ Token test error:', error);
                          setModalState({
                            isOpen: true,
                            title: '❌ Token Test Error',
                            content: '❌ Token test error: ' + error.message
                          });
                        }
                      }}
                    >
                      Test Token (etsy-users-me)
                    </button>
                    
                    <button 
                      className="btn"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('etsy_access_token');
                          if (!token) {
                            setModalState({
                              isOpen: true,
                              title: '❌ No Token Found',
                              content: 'Please connect to Etsy first.'
                            });
                            return;
                          }
                          
                          logger.log('🔍 Testing Variation Images API...');
                          const response = await fetch('/.netlify/functions/etsy-variation-images/846621034', {
                            headers: {
                              'Authorization': `Bearer ${token}`
                            }
                          });
                          
                          if (response.ok) {
                            const data = await response.json();
                            logger.log('✅ Variation Images API Response:', data);
                            
                            let message = '🎯 VARIATION IMAGES API TEST\n';
                            message += '=====================================\n\n';
                            message += `📊 Count: ${data.count || 0}\n`;
                            message += `📋 Results: ${data.results?.length || 0} variation images\n\n`;
                            
                            if (data.results && data.results.length > 0) {
                              message += '🔗 OFFICIAL ETsy MAPPINGS:\n';
                              message += '=====================================\n';
                              data.results.forEach((variation, index) => {
                                message += `🖼️ Variation ${index + 1}:\n`;
                                message += `   📍 Property ID: ${variation.property_id}\n`;
                                message += `   🏷️ Value ID: ${variation.value_id}\n`;
                                message += `   📏 Value: ${variation.value}\n`;
                                message += `   🖼️ Image ID: ${variation.image_id}\n\n`;
                              });
                            } else {
                              message += '⚠️ No variation images found - will use index-based correlation\n';
                            }
                            
                            setModalState({
                              isOpen: true,
                              title: '🎯 Variation Images API Test',
                              content: message
                            });
                          } else {
                            const errorData = await response.json();
                            console.error('❌ Variation Images API failed:', errorData);
                            
                            setModalState({
                              isOpen: true,
                              title: '❌ Variation Images API Failed',
                              content: `❌ Variation Images API failed: ${errorData.error || response.statusText}`
                            });
                          }
                        } catch (error) {
                          console.error('❌ Variation Images test error:', error);
                          
                          setModalState({
                            isOpen: true,
                            title: '❌ Variation Images Test Error',
                            content: '❌ Variation Images test error: ' + error.message
                          });
                        }
                      }}
                    >
                      Test Variation Images API
                    </button>
                    
                    <button 
                      className="btn"
                      onClick={async () => {
                        try {
                          // Get data from database (UNIFIED)
                          const response = await fetch('/.netlify/functions/local-db/unified-stickers/846621034');
                          
                          if (response.ok) {
                            const responseData = await response.json();
                            const dbData = responseData.data;
                            
                            // Parse the JSON strings from database
                            const listingData = JSON.parse(dbData.listing_data);
                            const imagesData = JSON.parse(dbData.images_data);
                            const inventoryData = JSON.parse(dbData.inventory_data);
                            const extractedVariations = JSON.parse(dbData.extracted_variations);
                            
                            // Extract sizes from variations
                            const sizes = extractedVariations.sizes || [];
                            
                            let message = '🖼️ IMAGE ANALYSIS - T-Rex Listing\n';
                            message += '=====================================\n\n';
                            message += `📋 Listing: ${listingData.title || 'Unknown'}\n`;
                            message += `🖼️ Images: ${imagesData.results?.length || 0} images\n`;
                            message += `📦 Inventory: ${inventoryData.products?.length || 0} products\n`;
                            message += `🎨 Sizes: ${sizes.length} (${sizes.join(', ')})\n\n`;
                            
                            message += `📊 SIZE-IMAGE CORRELATION:\n`;
                            message += '=====================================\n';
                            message += `Current ratio: ${imagesData.results?.length || 0} images / ${sizes.length} sizes = ${((imagesData.results?.length || 0) / sizes.length).toFixed(1)}\n\n`;
                            
                            if (sizes.length > 0) {
                              message += `🎯 INDEX-BASED MAPPING:\n`;
                              sizes.forEach((size, index) => {
                                const imageIndex = index + 1; // Skip first image (overall view)
                                const image = imagesData.results?.[imageIndex];
                                if (image) {
                                  message += `   ${size} → Image ${imageIndex + 1} (${image.url_570xN ? 'Available' : 'Missing'})\n`;
                                } else {
                                  message += `   ${size} → Image ${imageIndex + 1} (No image available)\n`;
                                }
                              });
                            } else {
                              message += `   Need manual mapping or admin assignment\n\n`;
                            }
                            
                            message += `🔗 Etsy Share URL: https://clothedeye.etsy.com/listing/846621034\n`;
                            message += `📊 Data Source: Database Only\n`;
                            
                            // Add image info to the message
                            if (imagesData.results?.length > 0) {
                              message += '\n🖼️ IMAGE GALLERY INFO:\n';
                              message += '=====================================\n';
                              imagesData.results.forEach((image, index) => {
                                message += `Image ${index + 1}:\n`;
                                message += `   URL: ${image.url_570xN || image.url_fullxfull}\n`;
                                message += `   Rank: ${image.rank || 'N/A'}\n`;
                                message += `   Size: ${sizes[index] || 'Unknown'}\n\n`;
                              });
                            }
                            
                            setModalState({
                              isOpen: true,
                              title: '🖼️ Image Analysis - T-Rex Listing',
                              content: message
                            });
                          } else {
                            throw new Error(`Database call failed: ${response.status}`);
                          }
                        } catch (error) {
                          console.error('❌ Image analysis failed:', error);
                          setModalState({
                            isOpen: true,
                            title: '❌ Image Analysis Failed',
                            content: '❌ Image analysis failed: ' + error.message
                          });
                        }
                      }}
                    >
                      Analyze Images & Sizes (Database)
                    </button>
                    
                    <button 
                      className="btn"
                      onClick={async () => {
                        try {
                          const result = await etsyDataService.fetchAndStoreListing('846621034');
                          
                          let message = '✅ Successfully fetched and stored T-Rex listing!\n\n';
                          message += `📋 Listing: ${result.data.listing?.title || 'Unknown'}\n`;
                          message += `🖼️ Images: ${result.data.images?.results?.length || 0} images\n`;
                          message += `📦 Inventory: ${result.data.inventory?.products?.length || 0} products\n`;
                          
                          if (result.data.extractedVariations) {
                            message += `🎨 Unique Sizes: ${result.data.extractedVariations.sizes.length}\n`;
                            message += `   ${result.data.extractedVariations.sizes.join(', ')}\n\n`;
                            message += `🎨 Unique Colors: ${result.data.extractedVariations.colors.length}\n`;
                            message += `   ${result.data.extractedVariations.colors.join(', ')}\n\n`;
                          }
                          
                          message += `💾 Database Result: ${result.databaseResult ? 'Success' : 'Failed'}\n`;
                          message += `🔗 Etsy Share URL: https://clothedeye.etsy.com/listing/846621034\n`;
                          message += `🏷️ Category: ${result.data.listing?.title?.toLowerCase().includes('trex') ? 'dinosaurs' : 'other'}\n\n`;
                          
                          // Show extracted dimensions from description (priority) and variations
                          const allDimensions = dimensionExtractor.extractAllDimensions(result.data);
                          if (Object.keys(allDimensions).length > 0) {
                            message += `📏 EXTRACTED DIMENSIONS:\n`;
                            Object.keys(allDimensions).forEach(sizeName => {
                              const dim = allDimensions[sizeName];
                              const source = dim.source || 'unknown';
                              message += `   ${sizeName}: ${dim.width}"w x ${dim.height}"h (${source})\n`;
                            });
                          } else {
                            message += `📏 No dimensions found in description or variations\n`;
                          }
                          
                          setModalState({
                            isOpen: true,
                            title: '✅ Fetch & Store Test Success',
                            content: message
                          });
                        } catch (error) {
                          console.error('❌ Fetch and store test failed:', error);
                          setModalState({
                            isOpen: true,
                            title: '❌ Fetch & Store Test Failed',
                            content: '❌ Fetch and store test failed: ' + error.message
                          });
                        }
                      }}
                    >
                      Test Fetch & Store (T-Rex)
                    </button>
                    
                    <button 
                      className="btn"
                      onClick={async () => {
                        try {
                          setModalState({
                            isOpen: true,
                            title: '🔄 Fetching 4 Shop Listings...',
                            content: '🔄 Starting to fetch 4 listings from your Etsy shop. This may take a minute...'
                          });
                          
                          // First get all listings, then only process the first 4
                          const shopData = await etsyDataService.fetchAllShopListings();
                          const listings = shopData.results || [];
                          const limitedListings = listings.slice(0, 4);
                          
                          logger.log(`📋 Found ${listings.length} total listings, processing first 4`);
                          
                          const results = [];
                          const errors = [];
                          
                          for (let i = 0; i < limitedListings.length; i++) {
                            const listing = limitedListings[i];
                            const listingId = listing.listing_id;
                            
                            try {
                              logger.log(`📦 Processing listing ${i + 1}/4: ${listingId} - ${listing.title}`);
                              
                              const result = await etsyDataService.fetchAndStoreListing(listingId);
                              results.push(result);
                              
                              // Add a small delay to avoid rate limiting
                              await new Promise(resolve => setTimeout(resolve, 100));
                              
                            } catch (error) {
                              console.error(`❌ Failed to process listing ${listingId}:`, error);
                              errors.push({
                                listingId,
                                error: error.message
                              });
                            }
                          }
                          
                          let message = '✅ Successfully fetched and stored 4 shop listings!\n\n';
                          message += `📋 Total Listings Found: ${listings.length}\n`;
                          message += `📦 Processed: 4 listings\n`;
                          message += `✅ Successfully Processed: ${results.length}\n`;
                          message += `❌ Failed to Process: ${errors.length}\n\n`;
                          
                          if (results.length > 0) {
                            message += `✅ SUCCESSFUL LISTINGS:\n`;
                            results.forEach((listing, index) => {
                              message += `   ${index + 1}. ${listing.listingId} - ${listing.data.listing?.title || 'Unknown'}\n`;
                            });
                            message += '\n';
                          }
                          
                          if (errors.length > 0) {
                            message += `❌ FAILED LISTINGS:\n`;
                            errors.forEach((error, index) => {
                              message += `   ${index + 1}. ${error.listingId} - ${error.error}\n`;
                            });
                          }
                          
                          setModalState({
                            isOpen: true,
                            title: '✅ Fetch 4 Complete',
                            content: message
                          });
                        } catch (error) {
                          console.error('❌ Fetch 4 listings failed:', error);
                          setModalState({
                            isOpen: true,
                            title: '❌ Fetch 4 Failed',
                            content: '❌ Failed to fetch 4 listings: ' + error.message
                          });
                        }
                      }}
                    >
                      Fetch 4 Shop Listings
                    </button>
                    
                    <button 
                      className="btn"
                      onClick={async () => {
                        try {
                          logger.log('🔍 Testing retrieval of stored T-Rex data...');
                          
                          const storedData = await etsyDataService.getStoredListingData('846621034');
                          
                          if (!storedData) {
                            setModalState({
                              isOpen: true,
                              title: '❌ No Stored Data Found',
                              content: '❌ No stored data found for T-Rex listing. Please run "Test Fetch & Store" first.'
                            });
                            return;
                          }
                          
                          let message = '✅ Successfully retrieved stored T-Rex data!\n\n';
                          message += `📋 Listing: ${storedData.listing_data?.title || 'Unknown'}\n`;
                          message += `🖼️ Images: ${storedData.images_data?.results?.length || 0} images\n`;
                          message += `📦 Inventory: ${storedData.inventory_data?.products?.length || 0} products\n`;
                          
                          if (storedData.extracted_variations) {
                            const variations = JSON.parse(storedData.extracted_variations);
                            message += `🎨 Unique Sizes: ${variations.sizes?.length || 0}\n`;
                            message += `   ${variations.sizes?.join(', ') || 'None'}\n\n`;
                            message += `🎨 Unique Colors: ${variations.colors?.length || 0}\n`;
                            message += `   ${variations.colors?.join(', ') || 'None'}\n\n`;
                          }
                          
                          message += `💾 Stored at: ${storedData.created_at}`;
                          
                          setModalState({
                            isOpen: true,
                            title: '✅ Data Retrieval Success',
                            content: message
                          });
                        } catch (error) {
                          console.error('❌ Data retrieval test failed:', error);
                          setModalState({
                            isOpen: true,
                            title: '❌ Data Retrieval Failed',
                            content: '❌ Data retrieval test failed: ' + error.message
                          });
                        }
                      }}
                    >
                      Test Retrieve Stored Data
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      
      {/* One Modal To Rule Them All */}
      <TheOneModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState({ isOpen: false, title: '', content: '' })}
        title={modalState.title}
        content={modalState.content}
        size="large"
      />

      {/* Loading Status Panel */}
      <LoadingStatusPanel
        isVisible={loadingPanelState.isVisible}
        progress={loadingPanelState.progress}
        messages={loadingPanelState.messages}
        onClose={() => loadingService.hide()}
      />
    </div>
  );
}

// AdminPanel has no props - it's a standalone admin page component
AdminPanel.propTypes = {};

export default AdminPanel; 